import type { PageType } from '../types/page-context.js';

/**
 * Normalize pathname for comparisons: decode URI, trim trailing slash (except root).
 */
export function normalizePathname(pathname: string): string {
  let path = pathname.trim();
  try {
    path = decodeURIComponent(path);
  } catch {
    // keep raw path
  }

  if (!path || path === '/') return '/';

  const withoutTrailing = path.replace(/\/+$/, '');
  return withoutTrailing.startsWith('/') ? withoutTrailing : `/${withoutTrailing}`;
}

/** `/home`, `/index`, optional locale prefix: `/uk/home`. */
const NAMED_HOME_PATTERN =
  /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:home|index)(?:\.html?)?$/i;

/**
 * Locale-only homepage: `/uk`, `/en`, `/uk-UA` (no further path segments).
 * Common on multilingual sites where `/` redirects to `/uk/`.
 */
const LOCALE_ROOT_PATTERN = /^\/[a-z]{2}(?:-[a-z]{2})?$/i;

export type IsHomepageOptions = {
  pathname?: string;
  /** When true, treat `link[rel=canonical]` pointing to site root as homepage hint. */
  doc?: Document;
  origin?: string;
};

/**
 * Whether the path is the site homepage.
 *
 * Matches:
 * - `/`
 * - `/home`, `/index` (+ `.html`)
 * - `/uk`, `/en`, `/uk-UA` (locale root)
 * - `/uk/home`, `/en/index`
 * - canonical URL pathname equals normalized path and canonical is a homepage path
 */
export function isHomepage(options: IsHomepageOptions | string = {}): boolean {
  const resolved =
    typeof options === 'string'
      ? { pathname: options }
      : options;

  const pathname = normalizePathname(
    resolved.pathname ??
      (typeof window !== 'undefined' ? window.location.pathname : '/'),
  );

  if (pathname === '/') return true;
  if (NAMED_HOME_PATTERN.test(pathname)) return true;
  if (LOCALE_ROOT_PATTERN.test(pathname)) return true;

  if (resolved.doc) {
    const canonical = resolved.doc
      .querySelector('link[rel="canonical"]')
      ?.getAttribute('href')
      ?.trim();

    if (canonical) {
      try {
        const base =
          resolved.origin ??
          (typeof window !== 'undefined' ? window.location.origin : 'https://local');
        const canonicalPath = normalizePathname(new URL(canonical, base).pathname);
        if (canonicalPath === pathname && isHomepage({ pathname: canonicalPath })) {
          return true;
        }
      } catch {
        // ignore invalid canonical
      }
    }
  }

  return false;
}

/** @deprecated Use `isHomepage({ pathname })` — kept for simple call sites. */
export function isHomepagePath(pathname: string): boolean {
  return isHomepage({ pathname });
}

const PRODUCT_PATH_PATTERNS = [
  /\/products?\//i,
  /\/p\//i,
  /\/item\//i,
  /\/catalog\/.+\/.+/i,
  /\/shop\/.+/i,
];

const CATEGORY_PATH_PATTERNS = [
  /\/collections?\//i,
  /\/category\//i,
  /\/categories\//i,
  /\/catalog\/?$/i,
];

const CART_PATH_PATTERNS = [/\/cart\/?$/i, /\/basket\/?$/i];
const CHECKOUT_PATH_PATTERNS = [/\/checkout/i, /\/order\/pay/i];
const BLOG_PATH_PATTERNS = [/\/blog\//i, /\/news\//i, /\/article\//i];
const SEARCH_PATH_PATTERNS = [/\/search/i, /\?.*\bq=/i];

/**
 * Classify page type from URL pathname (sync heuristics).
 * Used for popup delay settings and URL-based page context.
 */
export function classifyPageTypeFromPathname(pathname: string): PageType {
  const normalized = normalizePathname(pathname);

  if (isHomepage({ pathname: normalized })) return 'home';

  for (const pattern of CHECKOUT_PATH_PATTERNS) {
    if (pattern.test(normalized)) return 'checkout';
  }
  for (const pattern of CART_PATH_PATTERNS) {
    if (pattern.test(normalized)) return 'cart';
  }
  for (const pattern of PRODUCT_PATH_PATTERNS) {
    if (pattern.test(normalized)) return 'product';
  }
  for (const pattern of CATEGORY_PATH_PATTERNS) {
    if (pattern.test(normalized)) return 'category';
  }
  for (const pattern of BLOG_PATH_PATTERNS) {
    if (pattern.test(normalized)) return 'blog';
  }
  for (const pattern of SEARCH_PATH_PATTERNS) {
    if (pattern.test(normalized)) return 'search';
  }

  return 'other';
}

/**
 * Nav / breadcrumb labels that must not be used as product/page title
 * for catalog search (e.g. first BreadcrumbList `itemprop=name`).
 */
const GENERIC_PAGE_TITLE_PATTERN =
  /^(home|homepage|index|main|start|головна|головна сторінка|главная|главная страница|inicio|accueil|startseite)$/i;

/** True when a title is a generic home/nav label, not a product/category name. */
export function isGenericPageTitle(title: string | undefined | null): boolean {
  const trimmed = title?.trim();
  if (!trimmed) return true;
  return GENERIC_PAGE_TITLE_PATTERN.test(trimmed);
}

/**
 * Last meaningful URL segment as a humanized fallback title
 * (`/farkop-na-acura-mdx/22103/` → `farkop na acura mdx`).
 */
export function titleFromPathname(pathname: string | undefined): string | undefined {
  if (!pathname) return undefined;

  const segments = normalizePathname(pathname).split('/').filter(Boolean);

  for (let i = segments.length - 1; i >= 0; i -= 1) {
    const raw = segments[i]!;
    if (/^\d+$/.test(raw)) continue;
    if (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(raw)) continue;

    let decoded = raw;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      // keep raw
    }

    const humanized = decoded.replace(/[-_+]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (humanized.length < 3 || isGenericPageTitle(humanized)) continue;
    return humanized;
  }

  return undefined;
}

/** Prefer product/H1 title; skip generic home labels; fall back to pathname slug. */
export type PageTitleCandidate = {
  text: string;
  /** Breadcrumb / ListItem `itemprop=name`. */
  inBreadcrumb?: boolean;
  /** Nested under brand / organization. */
  inBrand?: boolean;
  /** `h1`, `.product-title`, etc. */
  preferred?: boolean;
};

/**
 * Pick the best product name from microdata `itemprop=name` candidates
 * (skips breadcrumbs, brands, and generic home labels).
 */
export function pickProductNameFromCandidates(
  candidates: readonly PageTitleCandidate[],
): string | undefined {
  const usable = candidates.filter(
    (c) =>
      Boolean(c.text.trim()) &&
      !c.inBreadcrumb &&
      !c.inBrand &&
      !isGenericPageTitle(c.text),
  );

  const preferred = usable.find((c) => c.preferred);
  if (preferred) return preferred.text.trim();

  // Longer names beat short brand-like leftovers (e.g. meta "Acura").
  let best: PageTitleCandidate | undefined;
  for (const candidate of usable) {
    if (!best || candidate.text.trim().length > best.text.trim().length) {
      best = candidate;
    }
  }
  return best?.text.trim();
}
